import { useCallback, useEffect, useRef, useState } from "react";

const normalizeHeading = (degrees) => ((degrees % 360) + 360) % 360;

const getScreenAngle = () => {
  if (typeof window === "undefined") return 0;
  if (typeof window.screen?.orientation?.angle === "number") {
    return window.screen.orientation.angle;
  }
  return typeof window.orientation === "number" ? window.orientation : 0;
};

const circularDifference = (from, to) =>
  ((to - from + 540) % 360) - 180;

/**
 * Tracks the direction in which the top of the phone is pointing.
 * iOS supplies a compass heading directly, while Android generally supplies
 * alpha in the opposite direction.
 */
export function useDeviceHeading() {
  const [heading, setHeading] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(null);
  const [requestingPermission, setRequestingPermission] = useState(false);
  const [error, setError] = useState("");
  const lastHeadingRef = useRef(null);
  const lastAbsoluteEventRef = useRef(0);
  const secureContext =
    typeof window !== "undefined" && window.isSecureContext === true;
  const hasOrientationApi =
    typeof window !== "undefined" && "DeviceOrientationEvent" in window;
  const supported = secureContext && hasOrientationApi;

  const requestPermission = useCallback(() => {
    if (!secureContext || !hasOrientationApi) {
      setPermissionGranted(false);
      setError(!secureContext ? "insecure" : "unsupported");
      return Promise.resolve(false);
    }

    if (
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function"
    ) {
      setRequestingPermission(true);
      setError("");
      try {
        const res = DeviceOrientationEvent.requestPermission();
        if (res && typeof res.then === "function") {
          return res
            .then((state) => {
              const granted = state === "granted";
              setPermissionGranted(granted);
              if (!granted) setError("denied");
              return granted;
            })
            .catch((err) => {
              console.warn("DeviceOrientation permission error handled:", err);
              setPermissionGranted(false);
              setError("denied");
              return false;
            })
            .finally(() => {
              setRequestingPermission(false);
            });
        }
      } catch (err) {
        console.warn("DeviceOrientation permission error handled:", err);
        setPermissionGranted(false);
        setError("denied");
        setRequestingPermission(false);
        return Promise.resolve(false);
      }
    }

    setPermissionGranted(true);
    setRequestingPermission(false);
    return Promise.resolve(true);
  }, [hasOrientationApi, secureContext]);

  useEffect(() => {
    if (!supported) {
      setPermissionGranted(false);
      setError(!secureContext ? "insecure" : "unsupported");
      return undefined;
    }

    const handleOrientation = (event) => {
      const isAbsoluteEvent =
        event.type === "deviceorientationabsolute" || event.absolute === true;

      let nextHeading = null;

      if (
        typeof event.webkitCompassHeading === "number" &&
        Number.isFinite(event.webkitCompassHeading)
      ) {
        // Safari already reports clockwise degrees from magnetic north.
        nextHeading = event.webkitCompassHeading;
      } else if (
        typeof event.alpha === "number" &&
        Number.isFinite(event.alpha)
      ) {
        // Alpha increases counter-clockwise. Compensate for landscape mode so
        // the arrow still represents the physical top edge of the screen.
        nextHeading = 360 - event.alpha + getScreenAngle();
      }

      if (nextHeading === null) return;

      // Some Android devices emit an "absolute" event whose alpha is null.
      // Only prefer it after receiving an actual usable compass value.
      if (isAbsoluteEvent) {
        lastAbsoluteEventRef.current = Date.now();
      } else if (Date.now() - lastAbsoluteEventRef.current < 500) {
        return;
      }

      nextHeading = normalizeHeading(nextHeading);
      setPermissionGranted(true);
      setError("");
      const previous = lastHeadingRef.current;

      // Interpolate across 0/360 instead of making the arrow spin the long way.
      const smoothed =
        previous === null
          ? nextHeading
          : normalizeHeading(previous + circularDifference(previous, nextHeading) * 0.3);

      if (
        previous === null ||
        Math.abs(circularDifference(previous, smoothed)) >= 0.5
      ) {
        lastHeadingRef.current = smoothed;
        setHeading(smoothed);
      }
    };

    // Some Android browsers expose the absolute event property but never emit
    // it, so listen to both and prefer absolute readings when they are present.
    window.addEventListener("deviceorientationabsolute", handleOrientation, true);
    window.addEventListener("deviceorientation", handleOrientation, true);

    return () => {
      window.removeEventListener("deviceorientationabsolute", handleOrientation, true);
      window.removeEventListener("deviceorientation", handleOrientation, true);
    };
  }, [hasOrientationApi, secureContext, supported]);

  useEffect(() => {
    if (!supported) return undefined;

    const needsUserGesture =
      typeof DeviceOrientationEvent !== "undefined" &&
      typeof DeviceOrientationEvent.requestPermission === "function";

    if (!needsUserGesture) {
      setPermissionGranted(true);
    } else {
      setPermissionGranted(null);
    }
    return undefined;
  }, [supported]);

  return {
    heading,
    supported,
    permissionGranted,
    requestingPermission,
    error,
    requestPermission,
  };
}

export default useDeviceHeading;
